import React, { useMemo, useState } from 'react';
import Svg, {
  Circle, G, Path, Text as SvgText,
} from 'react-native-svg';
import { View, Text } from 'react-native';
import Colors from '../../constants/Colors';

type ChartDatum = {
  index: number;
  value: number;
  date: string;
  label: string;
};

const SEGMENT_COLORS = [
  Colors.light.hereworksGreen400,
  Colors.light.hereworksYellow500,
  Colors.light.hereworksBlue400,
  Colors.light.hereworksOrange,
  Colors.light.hereworksPurple500,
  Colors.light.hereworksRed300,
  Colors.light.hereworksExtraDarkTeal,
];

const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInRadians: number,
) => ({
  x: centerX + radius * Math.cos(angleInRadians),
  y: centerY + radius * Math.sin(angleInRadians),
});

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const describePieSlice = (
  radius: number,
  startAngle: number,
  endAngle: number,
) => {
  const start = polarToCartesian(0, 0, radius, startAngle);
  const end = polarToCartesian(0, 0, radius, endAngle);
  const angleDelta = endAngle - startAngle;
  const largeArcFlag = angleDelta > Math.PI ? 1 : 0;

  return `M 0 0 L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y} Z`;
};

const PieChart = ({ chartData }: { chartData: ChartDatum[] }) => {
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const svgSize = useMemo(() => {
    if (!containerWidth) return 256;
    return Math.max(180, Math.min(containerWidth, 360));
  }, [containerWidth]);

  const padding = Math.max(20, svgSize * 0.11);
  const center = svgSize / 2;
  const radius = center - padding;
  const labelRadius = radius + Math.max(10, radius * 0.12);

  const total = chartData.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return null;

  let cumulativeValue = 0;
  const segments = chartData.map((item, i) => {
    const startAngle = (cumulativeValue / total) * 2 * Math.PI - Math.PI / 2;
    const endAngle = ((cumulativeValue + item.value) / total) * 2 * Math.PI - Math.PI / 2;
    const midAngle = startAngle + ((endAngle - startAngle) / 2);

    cumulativeValue += item.value;

    const labelPos = polarToCartesian(0, 0, labelRadius, midAngle);
    const label = `${item.value}`;
    const textAnchor: 'start' | 'end' = Math.cos(midAngle) >= 0 ? 'start' : 'end';
    const minBound = -center + 6;
    const maxBound = center - 6;
    const estimatedTextWidth = Math.max(8, label.length * 7);

    const clampedX = textAnchor === 'start'
      ? clamp(labelPos.x, minBound, maxBound - estimatedTextWidth)
      : clamp(labelPos.x, minBound + estimatedTextWidth, maxBound);

    const clampedY = clamp(labelPos.y, minBound + 8, maxBound - 8);

    return {
      item,
      color: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
      startAngle,
      endAngle,
      labelPos: {
        x: clampedX,
        y: clampedY,
      },
      label,
      textAnchor,
    };
  });

  return (
    <View
      onLayout={(event) => {
        const nextWidth = event.nativeEvent.layout.width;
        if (nextWidth > 0 && Math.abs(nextWidth - containerWidth) > 1) {
          setContainerWidth(nextWidth);
        }
      }}
      style={{ width: '100%', alignSelf: 'stretch' }}
    >
      <Svg width={'100%'} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
        <G transform={`translate(${center} ${center})`}>
          {segments.length === 1
            ? <Circle r={radius} fill={segments[0].color} />
            : segments.map((segment) => <Path
              key={`segment_${segment.item.index}`}
              d={describePieSlice(radius, segment.startAngle, segment.endAngle)}
              fill={segment.color}
            />)}
          {segments.map((segment) => <SvgText
            key={`label_${segment.item.index}`}
            x={segment.labelPos.x}
            y={segment.labelPos.y}
            fontSize={12}
            fill={'#000000'}
            alignmentBaseline="middle"
            textAnchor={segment.textAnchor}
          >
            {segment.label}
          </SvgText>)}
        </G>
      </Svg>
      <View style={{ marginTop: 16 }}>
          {chartData.map((item, i) => (
            <View key={item.index} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
              <View
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
                  marginRight: 8,
                }}
              />
              <Text style={{ fontSize: 12, color: '#333' }}>{`${item.label}: ${item.value}`}</Text>
            </View>
          ))}
        </View>
      </View>
  );
};

export default PieChart;
